import { useEffect, useState } from 'react';
import { Banknote, ShoppingCart } from 'lucide-react';
import CreditPurchaseModal, { type PurchaseTab } from './CreditPurchaseModal';
import { MONEY } from '@kioskgaming/ui';

export interface CreditPurchaseTriggerProps {
  costRate?: number;
  variant?: 'card' | 'button';
  className?: string;
  creditBalance?: number | null;
  creditBalanceLoading?: boolean;
  onlineBalanceNet?: number | null;
  onlineBalanceLoading?: boolean;
  onBalancesRefresh?: () => void | Promise<void>;
  conversionCostRate?: number | null;
  /** When true, navigates to checkout route on mount. */
  defaultOpen?: boolean;
}

export default function CreditPurchaseTrigger({
  costRate,
  variant = 'card',
  className = '',
  creditBalance,
  creditBalanceLoading,
  onlineBalanceNet,
  onlineBalanceLoading,
  onBalancesRefresh,
  conversionCostRate,
  defaultOpen = false,
}: CreditPurchaseTriggerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [modalInitialTab, setModalInitialTab] = useState<PurchaseTab>('crypto');

  function openModal(tab: PurchaseTab) {
    setModalInitialTab(tab);
    setOpen(true);
  }

  useEffect(() => {
    if (defaultOpen) {
      setModalInitialTab('crypto');
      setOpen(true);
    }
  }, [defaultOpen]);

  if (variant === 'button') {
    return (
      <>
        <div className={`flex flex-col items-stretch gap-2 ${className}`}>
          <button
            type="button"
            onClick={() => openModal('crypto')}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-blue-700"
          >
            <ShoppingCart className="h-4 w-4 shrink-0" aria-hidden />
            {MONEY.addUsd}
          </button>
          <button
            type="button"
            onClick={() => openModal('balance')}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-blue-600/40 bg-white px-4 py-2.5 text-sm font-semibold text-blue-800 shadow-sm hover:bg-blue-50"
          >
            <Banknote className="h-4 w-4 shrink-0" aria-hidden />
            {MONEY.buyCredits}
          </button>
        </div>
        <CreditPurchaseModal
          open={open}
          onClose={() => setOpen(false)}
          costRate={costRate}
          creditBalance={creditBalance}
          creditBalanceLoading={creditBalanceLoading}
          onlineBalanceNet={onlineBalanceNet}
          onlineBalanceLoading={onlineBalanceLoading}
          onBalancesRefresh={onBalancesRefresh}
          initialTab={modalInitialTab}
          conversionCostRate={conversionCostRate}
        />
      </>
    );
  }

  return (
    <>
      <div className={`rounded-xl border border-blue-100 bg-white p-6 shadow-sm ${className}`}>
        <h2 className="text-sm font-semibold text-gray-900">{MONEY.addUsd}</h2>
        <p className="mt-1 text-sm text-gray-500">
          Add USD with crypto, or turn your USD wallet balance into credits (no fee).
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => openModal('crypto')}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-blue-700"
          >
            <ShoppingCart className="h-4 w-4" aria-hidden />
            {MONEY.addUsd}
          </button>
          <button
            type="button"
            onClick={() => openModal('balance')}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-600/40 bg-white px-4 py-2.5 text-sm font-semibold text-blue-800 shadow-sm hover:bg-blue-50"
          >
            <Banknote className="h-4 w-4" aria-hidden />
            {MONEY.buyCredits}
          </button>
        </div>
      </div>
      <CreditPurchaseModal
        open={open}
        onClose={() => setOpen(false)}
        costRate={costRate}
        creditBalance={creditBalance}
        creditBalanceLoading={creditBalanceLoading}
        onlineBalanceNet={onlineBalanceNet}
        onlineBalanceLoading={onlineBalanceLoading}
        onBalancesRefresh={onBalancesRefresh}
        initialTab={modalInitialTab}
        conversionCostRate={conversionCostRate}
      />
    </>
  );
}
